import type { MediaPlanConfigBehavior } from '../media-plan-config-registry';
import { normalizeGoogleAdsCustomerId } from 'shared/src/google-ads/google-ads-types';

// Mirrors Beeswax's prompt but names Google Ads' bidding fields: TARGET_CPA / TARGET_ROAS carry an
// extra target value, emitted only when that strategy is selected. Lives here with the other Google
// Ads frontend pieces (preview card) rather than in the generic registry.
// TODO[ch]: revisit whether this belongs in the frontend at all — same pattern as Beeswax, but it
// could likely be bundled into the backend adapter. Should be resolved alongside Beeswax, not just
// Google Ads, so the two stay consistent.
export const googleAdsConfigBehavior: MediaPlanConfigBehavior = {
  buildProposePrompt: s => {
    // Multi-campaign split: pass a `campaigns` array to proposeGoogleAds (one per campaign, each with
    // its own bidding/geo and a budget_share). budget_share is price-denominated; the server converts
    // it to the media budget (margin removed), derives each daily budget from it, and reconciles the
    // converted shares to the line-item media budget.
    if (s.campaigns && s.campaigns.length > 1) {
      return [
        'The user has finalized a MULTI-CAMPAIGN split in the workspace card. Create one Google Ads ' +
          'campaign per entry below: call proposeGoogleAds ONCE with a `campaigns` array, then call ' +
          'createGoogleAdsMediaPlan with the resolved entities + notices it returns. Each campaigns[] ' +
          'entry MUST set campaign_mode="create", a campaign_budget_payload (name + delivery_method — ' +
          'the server overrides amount_dollars from budget_share), a campaign_payload, an ' +
          'ad_group_payload, and budget_share. budget_share is price-denominated; the server converts ' +
          'it to the media budget (margin removed), derives each campaign’s daily budget from it, and ' +
          'reconciles the converted shares to the line-item media budget.',
        `- account_id=${normalizeGoogleAdsCustomerId(s.accountId ?? '')}`,
        ...(s.managerAccountId
          ? [`- manager_account_id=${normalizeGoogleAdsCustomerId(s.managerAccountId)}`]
          : []),
        `- line_item.type=${s.lineItemType}`,
        `- line_item.budget_type=${s.budgetType}`,
        ...s.campaigns.map((c, i) => {
          const parts = [
            `name=${c.name ?? '(default from line item + suffix)'}`,
            `bidding=${c.biddingStrategy}`,
            ...(c.biddingStrategy === 'TARGET_CPA' ? [`target_cpa_dollars=${c.targetCpa}`] : []),
            ...(c.biddingStrategy === 'TARGET_ROAS' ? [`target_roas=${c.targetRoas}`] : []),
            `budget_share=${c.budgetShare}`,
            `geo=${c.geo ? c.geo : '(none)'}`
          ];
          return `- campaign ${i + 1}: ${parts.join('; ')}`;
        }),
        s.tracking
          ? `- campaign.tracking_template=${s.tracking}`
          : '- campaign.tracking_template=(none)',
        'The campaign name is derived server-side; set each campaign_payload.name to ONLY the distinguishing suffix (e.g. "Brand", "NonBrand", or a geo label) — the server appends it as a 4th segment. Default dates from the execution line item.'
      ].join('\n');
    }
    return [
      'The user has finalized the configuration in the workspace card. Use exactly these values:',
      `- account_id=${normalizeGoogleAdsCustomerId(s.accountId ?? '')}`,
      ...(s.managerAccountId
        ? [`- manager_account_id=${normalizeGoogleAdsCustomerId(s.managerAccountId)}`]
        : []),
      `- campaign_mode=${s.campaignMode}${s.campaignId ? ` (existing id ${s.campaignId})` : ''}`,
      `- line_item.type=${s.lineItemType}`,
      `- line_item.budget_type=${s.budgetType}`,
      `- line_item.bidding.strategy=${s.biddingStrategy}`,
      ...(s.biddingStrategy === 'TARGET_CPA'
        ? [`- line_item.bidding.target_cpa_dollars=${s.targetCpa}`]
        : []),
      ...(s.biddingStrategy === 'TARGET_ROAS'
        ? [`- line_item.bidding.target_roas=${s.targetRoas}`]
        : []),
      ...(s.biddingStrategy === 'MAXIMIZE_CLICKS' && s.maxCpc !== undefined
        ? [`- line_item.bidding.max_cpc_dollars=${s.maxCpc}`]
        : []),
      // Google Ads is a CPC platform — no cpm_bid is emitted (the ad-group cpc_bid_dollars is
      // derived server-side from the line item's planned unit cost).
      s.tracking
        ? `- campaign.tracking_template=${s.tracking}`
        : '- campaign.tracking_template=(none)',
      s.geo && s.geo.length > 0 ? `- targeting.geo=${s.geo.join(', ')}` : '- targeting.geo=(none)',
      'Call proposeGoogleAds ONCE using these values, then call createGoogleAdsMediaPlan with the resolved entities + notices it returns. The campaign name is derived server-side (do NOT set campaign_payload.name to convey it — it is overwritten). Default the ad group and budget names and the dates from the execution line item context, and the daily budget from the execution line item PRICE ÷ flight (price-denominated — the server converts it to media budget, margin removed).'
    ].join('\n');
  }
};
